import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { PhoneFilterOptions } from '@app/akita/api/products/models/phone-filter-options.model';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import {
  generateFiltersID,
  parseProductSearchFilters,
  ProductSearchFilters,
  ProductSearchSortMode,
  PRODUCT_SORTING_BEST_SALES,
  PRODUCT_SORTING_CHEAPEST_FIRST,
  PRODUCT_SORTING_CHEAPEST_LAST,
} from '@app/akita/api/products/models/product-search.filters.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';

import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { BrandIconPictureComponent } from '@app/shared/components/brand-icon-picture/brand-icon-picture.component';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { SearchSortBy } from '@app/shared/models/api/listing/search/search-sort-by.enum';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  Subscription,
  tap,
} from 'rxjs';

const BEST_BRANDS = ['Apple', 'Samsung', 'Dell', 'Lenovo', 'HP', 'Microsoft'];
const BEST_PHONE_BRANDS = ['Apple', 'Samsung'];
const BEST_LAPTTOP_BRANDS = ['Apple', 'Dell', 'Lenovo', 'HP', 'Microsoft'];

@Component({
  selector: 'app-product-brand-selector',
  templateUrl: './product-brand-selector.template.html',
  styleUrls: ['./product-brand-selector.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    PopsySkeletonComponent,
    BrandIconPictureComponent,
  ],
})
export class ProductBrandSelectorComponent implements OnDestroy, OnChanges {
  @Input() public phoneFilterOptions?: PhoneFilterOptions | null;
  @Input() public options?: ProductSearchFilterOptions | null;

  @Input() public smallMode?: boolean | null = false;

  @Input() public mobileMode?: boolean | null = false;
  @Input() public gridMode?: boolean | null = false;
  @Input() public pillMode?: boolean | null = false;
  @Input() public loading?: boolean | null;
  @Input() public onlyBestBrands?: boolean | null = false;
  @Input() public onlyBestPhoneBrands?: boolean | null = false;
  @Input() public onlyBestLaptopBrands?: boolean | null = false;

  @Output() public readonly filterBrandChanged: EventEmitter<Array<string>>;

  public readonly phoneSearchFragment$: Observable<Array<string>>;
  public country: string;

  public productOptions$: Observable<ProductSearchFilterOptions>;
  public fallbackOptions$: Observable<ProductSearchFilterOptions>;
  public readonly fetchingProductOptions$: Observable<boolean>;
  public readonly productOptionsError$: Observable<ApiError | null>;

  public searchParams$: Observable<Params>;
  public searchOptionsPath: string | null;

  public sortedBrands$: Observable<Array<string> | null>;

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  // public phoneProductFragment: Array<string>;
  public readonly queryParamsToKeep$: Observable<Params>;

  constructor(
    private readonly zone: NgZone,
    private readonly translateService: TranslocoService,

    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.filterBrandChanged = new EventEmitter();

    this.searchOptionsPath = null;

    this.country = this.validateCountryCode(akitaRouterQuery.country);

    this.phoneSearchFragment$ = this.akitaRouterQuery.selectPhoneSearchFragment();

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.searchParams$ = this.akitaRouterQuery.selectQueryParams().pipe(
      tap((params: Params) => {
        const path = this.getFiltersPathFromQueryParams(params);
        this.searchOptionsPath = path;
      })
    );

    this.productOptions$ = this.akitaProductsQuery.selectProductFilters(
      this.searchOptionsPath
    );
    this.fallbackOptions$ = this.akitaProductsQuery.selectProductFilters('-');

    this.sortedBrands$ = this.getSortedBrands(this.productOptions$);

    this.fetchingProductOptions$ = this.akitaProductsQuery.selectIsFetchingProductFilters(
      this.searchOptionsPath
    );
    this.productOptionsError$ = this.akitaProductsQuery.selectErrorFetchingProductFilters(
      this.searchOptionsPath
    );

    // if we do not have availableProductFilters on the state
    if (
      !this.akitaProductsQuery.getProductFilters(this.searchOptionsPath)?.brand.length &&
      !this.akitaProductsQuery.getIsFetchingProductFilters(this.searchOptionsPath)
    ) {
      if (this.akitaRouterQuery.isBrowser) {
        this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
      } else {
        this.fetchProductOptionsInBG();
      }
    }
  }

  private fetchProductOptionsInBG(): void {
    this.subscriptions.add(
      this.akitaProductsService.getAvailableSearchOptionsAsync(
        this.proccessFilters(this.akitaRouterQuery.paramsAndQueryParams)
      )
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.phoneFilterOptions) {
      const path = this.getFiltersPathFromQueryParams(
        this.akitaRouterQuery.paramsAndQueryParams
      );
      this.searchOptionsPath = path;

      this.productOptions$ = this.akitaProductsQuery.selectProductFilters(
        this.searchOptionsPath
      );
      this.fallbackOptions$ = this.akitaProductsQuery.selectProductFilters('-');

      this.sortedBrands$ = this.getSortedBrands(this.productOptions$);

      this.sortedBrands$ = combineLatest([
        this.getSortedBrands(this.productOptions$),
        this.getSortedBrands(this.fallbackOptions$),
      ]).pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        map(([searchBrands, fallbackBrands]) =>
          searchBrands?.length ? searchBrands : fallbackBrands || BEST_PHONE_BRANDS
        )
      );

      // if we do not have availableProductFilters on the state
      if (
        !this.akitaProductsQuery.getProductFilters(this.searchOptionsPath)?.brand?.length
      ) {
        if (this.akitaRouterQuery.isBrowser) {
          this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
        } else {
          this.fetchProductOptionsInBG();
        }
      }
    }
  }

  public getImageUrl(brand: string): string {
    // eslint-disable-next-line max-len
    return `https://firebasestorage.googleapis.com/v0/b/popsygae.appspot.com/o/Png-Brands-Images%2F${brand}.png?alt=media&token=14f6dec7-6448-4f64-8c1f-6919068d9007`;
  }

  public validateCountryCode(country: string): string {
    if (country) {
      country = country.toUpperCase() || '';
      if (
        country !== 'SA' &&
        country !== 'AE' &&
        country !== 'OM' &&
        country !== 'KW' &&
        country !== 'US' &&
        country !== 'MX' &&
        country !== 'CA' &&
        country !== 'BR'
      ) {
        country = 'US'; // fallback
      }
    }
    return country;
  }

  public switchBrandFilter(brand?: string | null): void {
    // MULTIPLE SELECTION NOT AVAILABLE
    if (brand === 'label_all') {
      // this.selectedModelsSubject.next([]);
      this.filterBrandChanged.emit([]);
    } else if (brand) {
      const selectedCategories = new Array(0);

      if (!this.phoneFilterOptions?.brands?.includes(brand)) {
        selectedCategories.push(brand);
      } else {
        selectedCategories.pop();
      }

      this.filterBrandChanged.emit(selectedCategories);
      // this.selectedModelsSubject.next(selectedModels);
    }

    this.changeDetector.markForCheck();
  }

  public productFragment(id: number | null): Array<string> {
    return this.akitaRouterQuery.getProductUrlFragment(`${id}`);
  }

  public trackByModel(index: number, brand?: any | null): string {
    return (brand as any)?.id || brand || `${index}`;
  }

  public capitalizeBrand(str: string): string | null {
    return str.charAt(0).toUpperCase().concat(str.slice(1).toLowerCase());
  }

  public getSearchUrlFragment(): Array<string> | string {
    return this.akitaRouterQuery.getPhoneSearchFragment();
  }

  public getCategoryParams(brand: string): Params | null {
    return {
      ...this.akitaRouterQuery.queryParamsToKeep,
      brand: brand,
    };
  }

  public getSortedBrands(
    result: Observable<ProductSearchFilterOptions>
  ): Observable<Array<string> | null> {
    return result.pipe(
      map((options) => {
        const results = options.brand;
        if (!options.brand.length) {
          return null;
        }

        const anyitems = [...results].filter((x) => x !== 'Apple' && x !== 'Samsung');

        // SHOW SAMSUNG SECOND
        [...results.filter((x) => x === 'Samsung')].reverse().forEach((item) => {
          anyitems.unshift(item);
        });

        // SHOW APPLE FIRST
        [...results.filter((x) => x === 'Apple')].reverse().forEach((item) => {
          anyitems.unshift(item);
        });

        if (this.onlyBestBrands) {
          return anyitems.filter((x) => BEST_BRANDS.includes(x));
        } else if (this.onlyBestPhoneBrands) {
          return anyitems.filter((x) => BEST_PHONE_BRANDS.includes(x));
        } else if (this.onlyBestLaptopBrands) {
          return anyitems.filter((x) => BEST_LAPTTOP_BRANDS.includes(x));
        }

        return anyitems;
      })
    );
  }

  public getFiltersPathFromQueryParams(queryParams: Params): string {
    const parsedFilters = this.proccessFilters(queryParams);
    const path = generateFiltersID(parsedFilters);

    return path;
  }

  private proccessFilters(queryParams: Params): ProductSearchFilters {
    let modelsQuery = queryParams.models || null;
    // const brandsQuery = queryParams.brands || null;
    let categoryQuery = queryParams.category || null;
    // filters on url fragments section and key
    switch (queryParams.section) {
      case 'category':
        categoryQuery = queryParams.key.toUpperCase();
        break;
      case 'model':
        modelsQuery = queryParams.key;
        break;
      default:
        break;
    }
    const colorsQuery = queryParams.colors || null;
    const storagesQuery = queryParams.storages || null;
    const textQuery = queryParams.text || null;
    const cpuQuery = queryParams.cpu || null;
    const graphicsQuery = queryParams.graphics || null;
    const launchDateQuery = queryParams.launch_date || null;
    const ramQuery = queryParams.ram || null;
    const screenSizeQuery = queryParams.screen_size || null;
    const storageTypeQuery = queryParams.storage_type || null;

    const sortByQuery = queryParams.sort_by || null;
    const minPriceQuery = queryParams.min_price || null;
    const maxPriceQuery = queryParams.max_price || null;

    const phoneFilters = { ...this.phoneFilterOptions };

    let productSort: ProductSearchSortMode | null = null;
    let productStorages: Array<string> | null = null;
    let productDates: Array<string> | null = null;

    if (modelsQuery) {
      const models = decodeURI(modelsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.models = models;
    } else {
      phoneFilters.models = [];
    }

    /* if (brandsQuery) {
      const brands = decodeURI(brandsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.brands = brands;
    } else {
      phoneFilters.brands = [];
    }
 */
    delete phoneFilters.brands; // we want to show all brands tags

    if (categoryQuery) {
      const categories = decodeURI(categoryQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.category = categories;
    } else {
      phoneFilters.category = [];
    }

    if (colorsQuery) {
      const colors = decodeURI(colorsQuery).replace(/[+_]/g, ' ').split('|');
      phoneFilters.colors = colors;
    } else {
      phoneFilters.colors = [];
    }

    if (cpuQuery) {
      const cpus = decodeURI(cpuQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.cpu = cpus;
    } else {
      phoneFilters.cpu = [];
    }

    if (graphicsQuery) {
      const graphics = decodeURI(graphicsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.graphics = graphics;
    } else {
      phoneFilters.graphics = [];
    }

    if (storagesQuery) {
      const storages = decodeURI(storagesQuery).replace(/\+/g, ' ').split('|');

      const parsedStorages: Array<number> = new Array(0);
      productStorages = new Array(0);
      for (const storage of storages) {
        parsedStorages.push(parseInt(storage, 10));
        productStorages.push(storage);
      }

      phoneFilters.storages = parsedStorages;
    } else {
      phoneFilters.storages = [];
    }

    if (launchDateQuery) {
      const launchDate = decodeURI(launchDateQuery).replace(/\+/g, ' ').split('|');

      const parsedDates: Array<number> = new Array(0);
      productDates = new Array(0);
      for (const date of launchDate) {
        parsedDates.push(parseInt(date, 10));
        productDates.push(date);
      }

      phoneFilters.launch_date = parsedDates;
    } else {
      phoneFilters.storages = [];
    }

    if (ramQuery) {
      const ram = decodeURI(ramQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.ram = ram;
    } else {
      phoneFilters.graphics = [];
    }

    if (screenSizeQuery) {
      const screenSize = decodeURI(screenSizeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.screen_size = screenSize;
    } else {
      phoneFilters.graphics = [];
    }

    if (storageTypeQuery) {
      const storageType = decodeURI(storageTypeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.storage_type = storageType;
    } else {
      phoneFilters.graphics = [];
    }

    if (sortByQuery) {
      const sortByNormalized = `${sortByQuery || ''}`.toUpperCase();
      if (sortByNormalized === PRODUCT_SORTING_BEST_SALES) {
        phoneFilters.sortBy = SearchSortBy.NOT_SET;
        productSort = PRODUCT_SORTING_BEST_SALES;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_FIRST ||
        sortByNormalized === SearchSortBy.PRICE_ASC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_ASC;
        productSort = PRODUCT_SORTING_CHEAPEST_FIRST;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_LAST ||
        sortByNormalized === SearchSortBy.PRICE_DESC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_DESC;
        productSort = PRODUCT_SORTING_CHEAPEST_LAST;
      }
    } else {
      phoneFilters.sortBy = null;
    }

    if (minPriceQuery) {
      phoneFilters.minPrice = parseInt(`${minPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.minPrice = undefined;
    }

    if (maxPriceQuery) {
      phoneFilters.maxPrice = parseInt(`${maxPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.maxPrice = undefined;
    }

    if (textQuery) {
      phoneFilters.text = textQuery || undefined;
    } else {
      phoneFilters.text = undefined;
    }

    const searchOptions = parseProductSearchFilters();
    searchOptions.model = phoneFilters.models;
    searchOptions.brand = phoneFilters.brands;
    searchOptions.category = phoneFilters.category;
    searchOptions.color = phoneFilters.colors;
    searchOptions.storage = productStorages;
    searchOptions.sorting = productSort;
    searchOptions.price_min = phoneFilters.minPrice;
    searchOptions.price_max = phoneFilters.maxPrice;
    searchOptions.text = phoneFilters.text;
    searchOptions.cpu = phoneFilters.cpu;
    searchOptions.graphics = phoneFilters.graphics;
    searchOptions.launch_date = phoneFilters.launch_date;
    searchOptions.ram = phoneFilters.ram;
    searchOptions.screen_size = phoneFilters.screen_size;
    searchOptions.storage_type = phoneFilters.storage_type;

    const language = `${this.translateService.getActiveLang() || ''}`.toLowerCase();
    if (language) {
      searchOptions.language = language;
    }

    const country = `${this.akitaRouterQuery.country || ''}`.toUpperCase();
    if (country) {
      searchOptions.country = country;
    }

    const validLocale = this.akitaProductsQuery.validateLocale(language, country);
    searchOptions.language = validLocale.language;
    searchOptions.country = validLocale.country;

    return searchOptions;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
